import _ from 'lodash'
import { t } from 'i18next'
import {
  ProductItemAvailabilityStatus,
  SourceSystem,
  productStatusSortOrder
} from '../../../constants'
import { CountryAlphaCodes } from '../../../constants/countryAlpha2Codes'
import {
  AvailableWarehouse,
  IAugmentedCatalogItemDto
} from '../../../services/PortfolioJourneys/PortfolioJourney.types'
import {
  DefaultWarehouseDto,
  PriceAndAvailabilityDto,
  StockDto,
  WarehouseDto
} from '../../../types/swaggerTypes'

export interface ICatalogItemDto extends IAugmentedCatalogItemDto {
  warehouseCodes: string[]
  warehouses: WarehouseDto[] | undefined
  // AUS specific properties
  displayOnRequest?: boolean
  shouldShowStock?: boolean
}

export interface ICatalogItemColumn {
  name: string
  viewName: string | JSX.Element
  width: string
  minWidth?: string
  maxWidth?: string
  isSortable?: boolean
  renderValue?: (catalogueItem: IAugmentedCatalogItemDto) => React.ReactNode
}

export enum CatalogueItemConstants {
  Available = 'Available',
  AvailableOnRequest = 'Available on request',
  InStock = 'In stock',
  RequestQuote = 'Request quote',
  OutOfStock = 'Out of stock',
  BackOrder = 'Back order',
  Unavailable = 'Unavailable'
}
/**
 * Get the stock/warehouse with the most stock in reserve
 */
export const getBestStockedWarehouse = (stocks: StockDto[]): StockDto => {
  return stocks.reduce<StockDto>(
    (prev: StockDto, curr: StockDto) =>
      curr.reservableStock > prev.reservableStock ? curr : prev,
    stocks[0]
  )
}

/**
 * Calculate availability score for sorting
 */
const getAvailabilityScore = (
  item: IAugmentedCatalogItemDto,
  portfolioCountryCode: string
): number => {
  if (
    !item.availableStatus ||
    item.availableStatus === ProductItemAvailabilityStatus.HIDDEN
  ) {
    return -1
  }

  const statusIndex = productStatusSortOrder.indexOf(item.availableStatus)
  const baseScore = (productStatusSortOrder.length - statusIndex) * 1000

  // Add score from default warehouse stock
  const defaultWarehouse = getDefaultWarehouseCode(portfolioCountryCode)
  const defaultWarehouseStock =
    item.stockLevels?.find((stock) => stock.warehouse === defaultWarehouse)
      ?.reservableStock || 0

  return baseScore + defaultWarehouseStock + (item.reservableStock || 0)
}

export const isStockAvailableInDefaultWarehouse = (
  item: IAugmentedCatalogItemDto,
  portfolioCountryCode: string
): boolean => {
  const defaultWarehouse = getDefaultWarehouseCode(portfolioCountryCode)
  const defaultWarehouseStock =
    item.stockLevels?.find((stock) => stock.warehouse === defaultWarehouse)
      ?.reservableStock || 0

  return defaultWarehouseStock > 0
}

/**
 * Sort catalog items by availability status and stock levels
 */
export const sortCatalogItemsByAvailability = (
  a: IAugmentedCatalogItemDto,
  b: IAugmentedCatalogItemDto,
  portfolioCountryCode: string
): number => {
  const scoreA = getAvailabilityScore(a, portfolioCountryCode)
  const scoreB = getAvailabilityScore(b, portfolioCountryCode)

  return scoreB - scoreA
}
/**
 * Australia-specific sort for catalog items
 */
export const sortCatalogItemsByAvailabilityAus = (
  a: IAugmentedCatalogItemDto,
  b: IAugmentedCatalogItemDto,
  portfolioCountryCode: string
): number => {
  const scoreA = getAvailabilityScore(a, portfolioCountryCode)
  const scoreB = getAvailabilityScore(b, portfolioCountryCode)

  if (scoreA !== scoreB) {
    return scoreB - scoreA
  }
  // If scores are equal but both items have stock, prioritize higher stock
  if (a.reservableStock && b.reservableStock) {
    return b.reservableStock - a.reservableStock
  }

  // Prioritize items with defined stock over undefined
  if (a.reservableStock && !b.reservableStock) return -1
  if (!a.reservableStock && b.reservableStock) return 1

  // If neither has stock, prioritize Sage items for AU
  if (
    a.item.sourceSystem === SourceSystem.Sage &&
    b.item.sourceSystem !== SourceSystem.Sage
  ) {
    return -1
  }
  if (
    a.item.sourceSystem !== SourceSystem.Sage &&
    b.item.sourceSystem === SourceSystem.Sage
  ) {
    return 1
  }

  return 0
}

/**
 * Get matching default warehouse codes
 */
export const getMatchingDefaultWarehouseCodes = (
  stockLevels: StockDto[],
  portfolioCountryCode: string,
  defaultWarehouses?: DefaultWarehouseDto[]
): string[] => {
  if (!defaultWarehouses?.length) {
    return [getDefaultWarehouseCode(portfolioCountryCode)]
  }

  const augmentedWarehouses = defaultWarehouses.map((w) => ({
    ...w,
    warehouse: w.warehouseCode
  }))

  const matchingCodes = _.intersectionBy(
    augmentedWarehouses,
    stockLevels,
    'warehouse'
  ).map((w) => w.warehouseCode)

  return matchingCodes.length
    ? matchingCodes
    : [getDefaultWarehouseCode(portfolioCountryCode)]
}

export const getCatalogueItemStatus = (
  catalogueItem: IAugmentedCatalogItemDto,
  portfolioCountryCode: string,
  priceAvailability?: PriceAndAvailabilityDto,
  forceInStock: boolean = false
) => {
  const { stockLevels, availableText, item } = catalogueItem
  const supplyProblemFlag = item?.supplyProblem
  const availableQuantity = priceAvailability?.availableQuantity
  let catalogueItemStatus = ''

  let stockLevel = 0
  if (!availableQuantity) {
    // ETL information
    const defaultWarehouse = getDefaultWarehouseCode(portfolioCountryCode)

    stockLevel =
      stockLevels?.find((stock) => stock.warehouse === defaultWarehouse)
        ?.reservableStock || 0
  } else {
    // live P&A information
    stockLevel = parseInt(availableQuantity, 0)
  }

  if (availableText === CatalogueItemConstants.AvailableOnRequest) {
    catalogueItemStatus = CatalogueItemConstants.RequestQuote
  } else if (catalogueItem.showDistributorInfo) {
    catalogueItemStatus = CatalogueItemConstants.InStock
  } else if (stockLevel > 0) {
    catalogueItemStatus = forceInStock
      ? `${stockLevel} packs available`
      : CatalogueItemConstants.InStock
  } else if (stockLevel <= 0) {
    catalogueItemStatus =
      supplyProblemFlag === 'Y' || catalogueItem.cannotOrder
        ? CatalogueItemConstants.OutOfStock
        : CatalogueItemConstants.BackOrder
  }

  return catalogueItemStatus
}

/**
 * Get default warehouse code by country
 */
export const getDefaultWarehouseCode = (
  portfolioCountryCode: string
): string => {
  switch (portfolioCountryCode) {
    case CountryAlphaCodes.Australia:
      return AvailableWarehouse.T07
    case CountryAlphaCodes.Ireland:
      return AvailableWarehouse.C06
    default:
      return AvailableWarehouse.C02
  }
}

export const getTooltipTranslation = (status: string): string => {
  const tooltipKey =
    productStatusTooltipTranslation[
      status as keyof typeof productStatusTooltipTranslation
    ]

  return t(tooltipKey) ?? ''
}

export const getSummarySectionHeaderTranslation = (status: string): string => {
  const headerKey =
    productSummarySectionHeader[
      status as keyof typeof productSummarySectionHeader
    ]
  return headerKey ? t(headerKey) : ''
}

export const catalogueItemStatusMapper: Record<
  string,
  ProductItemAvailabilityStatus
> = {
  [CatalogueItemConstants.BackOrder]: ProductItemAvailabilityStatus.AVAILABLE,
  [CatalogueItemConstants.InStock]: ProductItemAvailabilityStatus.AVAILABLE,
  [CatalogueItemConstants.RequestQuote]:
    ProductItemAvailabilityStatus.AVAILABLE_ON_REQUEST,
  [CatalogueItemConstants.OutOfStock]: ProductItemAvailabilityStatus.UNAVAILABLE
}

export const productStatusTooltipTranslation = {
  [CatalogueItemConstants.RequestQuote]:
    'product_catalogue:status_tooltip.request_quote',
  [CatalogueItemConstants.BackOrder]:
    'product_catalogue:status_tooltip.pre_order',
  [CatalogueItemConstants.OutOfStock]:
    'product_catalogue:status_tooltip.out_of_stock',
  [CatalogueItemConstants.InStock]: 'product_catalogue:status_tooltip.in_stock'
}

export const productSummarySectionHeader = {
  [CatalogueItemConstants.Available]: 'basket:summary',
  [CatalogueItemConstants.BackOrder]: 'basket:summary',
  [CatalogueItemConstants.RequestQuote]:
    'product_detail:quantity_quote_request',
  [CatalogueItemConstants.OutOfStock]: 'product_detail:supply_disruption'
}
